import type { OrderDetailResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useCreatePartnerReviewMutation } from "Order/Api/Mutation/useCreatePartnerReviewMutation";
import {
	type CreateReviewFormModel,
	getCreateReviewFormSchema,
} from "Order/Components/Form/Schema/reviewFormSchema";
import { usePartnersListQuery } from "Partner/Api/Query/usePartnersListQuery";
import { transformAndSortPartners } from "Partner/Utilities/transformSortPartners";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { RadioReviewButtons } from "Shared/Components/Form/CustomFormComponent/RadioReviewButtons";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid2 } from "@mui/material";
import { useMemo } from "react";
import { useForm } from "react-hook-form";

const { partnerReviewForm } = Resources.base.order;

type CreateReviewFormProps = {
	order: OrderDetailResponseDto;
	reviewedPartnerIds?: number[];
};

export function CreateReviewForm({
	order,
	reviewedPartnerIds,
}: CreateReviewFormProps) {
	const { t } = useResource();
	const formDefaultValues = useMemo(
		() => ({
			rating: "",
			description: "",
			partnerId: order.partner?.id,
		}),
		[order.partner?.id],
	);
	const form = useForm<CreateReviewFormModel>({
		resolver: zodResolver(getCreateReviewFormSchema()),
		defaultValues: formDefaultValues,
	});
	const {
		control,
		formState: { errors },
	} = form;

	const { data: partners, isLoading: isLoadingPartners } = usePartnersListQuery(
		{
			marketId: order.market?.id,
			verticals: order.verticalId.toString(),
		},
	);

	const { mutate: createReview, isPending } = useCreatePartnerReviewMutation(
		order.id,
	);

	const { handleServerError } = useHandleServerError(form, true);

	const handleSubmitCreate = () => {
		handleServerError(createReview, {
			onSuccess: () => {
				form.reset(formDefaultValues);
			},
		});
	};

	const partnerSelectOptions = useMemo(() => {
		const sortedPartners = transformAndSortPartners(
			partners?.data ?? [],
			order.region?.name,
		);

		const optionsWithDisabled = sortedPartners.map((partner) => ({
			...partner,
			disabled: !!reviewedPartnerIds?.includes(partner.value as number),
		}));

		return optionsWithDisabled;
	}, [partners, order.region, reviewedPartnerIds]);

	return (
		<>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12 }}>
					<Select
						id="partnerId"
						name="partnerId"
						label={t(partnerReviewForm.partner)}
						variant="outlined"
						options={partnerSelectOptions}
						groupBy="region"
						control={control}
						errors={errors}
						disabled={isLoadingPartners}
						numeric
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<RadioReviewButtons
						id="rating"
						name="rating"
						label={t(partnerReviewForm.rating)}
						control={control}
						errors={errors}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Input
						id="description"
						name="description"
						label={t(partnerReviewForm.description)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						multiline
					/>
				</Grid2>
			</Grid2>

			<Box mt={2}>
				<Button
					onClick={handleSubmitCreate}
					loading={isPending}
					variant="contained"
				>
					{t(partnerReviewForm.add)}
				</Button>
			</Box>
		</>
	);
}
